import { api } from "@/shared/api/client";
import {
    StockToolCreateDTO,
    StockToolGetDTO,
    StockToolsGetDTO,
    StockToolUpdateDTO,
    PageStockToolsGetDTO,
} from "@/shared/types/inventory/stock";

export interface StockToolsQueryFilter {
    toolName?: string;
    status?: string;
    stockId?: string;
    username?: string;
}

/**
 * Serviço para Ferramentas de Estoque (Stock Tools)
 *
 * Rotas:
 *  GET    /stocks/tools                                          → lista todos (paginado)
 *  GET    /stocks/tools/{tool-id}                                → busca por ID
 *  GET    /storages/{storage-id}/stock/tools                     → lista por almoxarifado
 *  POST   /storages/{storage-id}/stock/tools                     → cria em almoxarifado
 *  PUT    /storages/{storage-id}/stock/tools/{tool-id}           → atualiza em almoxarifado
 *  DELETE /storages/{storage-id}/stock/tools/{tool-id}           → deleta de almoxarifado
 *  GET    /projects/{project-id}/stock/tools                     → lista por projeto
 *  GET    /projects/{project-id}/stock/tools/{tool-id}           → busca em estoque de projeto
 *  POST   /projects/{project-id}/stock/tools                     → cria em estoque de projeto
 *  PUT    /projects/{project-id}/stock/tools/{tool-id}           → atualiza em estoque de projeto
 *  DELETE /projects/{project-id}/stock/tools/{tool-id}           → deleta de estoque de projeto
 */
export const stockToolService = {
    // ─── Listagem geral ─────────────────────────────────────────

    /**
     * Lista todas as ferramentas de estoque com paginação
     * GET /stocks/tools
     */
    getAll: async (
        params: {
            page: number;
            size: number;
            sort?: string;
            filter?: StockToolsQueryFilter;
        },
        signal?: AbortSignal
    ): Promise<PageStockToolsGetDTO> => {
        const response = await api.get<PageStockToolsGetDTO>(
            "/stocks/tools",
            {
                params: {
                    page: params.page - 1,
                    size: params.size,
                    sort: params.sort || "id,desc",
                    ...params.filter,
                },
                signal,
            }
        );
        return response.data;
    },

    /**
     * Busca ferramenta de estoque por ID
     * GET /stocks/tools/{tool-id}
     */
    getById: async (toolId: string): Promise<StockToolGetDTO> => {
        const response = await api.get<StockToolGetDTO>(
            `/stocks/tools/${toolId}`
        );
        return response.data;
    },

    /**
     * Devolve uma ferramenta para o estoque
     * PUT /stocks/tools/{tool-id}/return
     */
    returnTool: async (toolId: string): Promise<StockToolGetDTO> => {
        const response = await api.put<StockToolGetDTO>(
            `/stocks/tools/${toolId}/return`
        );
        return response.data;
    },

    // ─── Storage Stock (Almoxarifado) ───────────────────────────

    /**
     * Lista ferramentas de estoque de um almoxarifado
     * GET /storages/{storage-id}/stock/tools
     */
    listByStorageId: async (
        storageId: string,
        params: {
            page: number;
            size: number;
            sort?: string;
            filter?: StockToolsQueryFilter;
        },
        signal?: AbortSignal
    ): Promise<PageStockToolsGetDTO> => {
        const response = await api.get<PageStockToolsGetDTO>(
            `/storages/${storageId}/stock/tools`,
            {
                params: {
                    page: params.page - 1,
                    size: params.size,
                    sort: params.sort || "id,desc",
                    ...params.filter,
                },
                signal,
            }
        );
        return response.data;
    },

    /**
     * Cria ferramenta em estoque de almoxarifado
     * POST /storages/{storage-id}/stock/tools
     */
    createInStorageStock: async (
        storageId: string,
        data: StockToolCreateDTO
    ): Promise<StockToolGetDTO> => {
        const response = await api.post<StockToolGetDTO>(
            `/storages/${storageId}/stock/tools`,
            data
        );
        return response.data;
    },

    /**
     * Atualiza ferramenta em estoque de almoxarifado
     * PUT /storages/{storage-id}/stock/tools/{tool-id}
     */
    updateInStorageStock: async (
        storageId: string,
        toolId: string,
        data: StockToolUpdateDTO
    ): Promise<StockToolGetDTO> => {
        const response = await api.put<StockToolGetDTO>(
            `/storages/${storageId}/stock/tools/${toolId}`,
            data
        );
        return response.data;
    },

    /**
     * Deleta ferramenta de estoque de almoxarifado
     * DELETE /storages/{storage-id}/stock/tools/{tool-id}
     */
    deleteFromStorageStock: async (storageId: string, toolId: string): Promise<void> => {
        await api.delete(`/storages/${storageId}/stock/tools/${toolId}`);
    },

    // ─── Project Stock (Estoque de Projeto) ─────────────────────

    /**
     * Lista ferramentas de estoque de um projeto
     * GET /projects/{project-id}/stock/tools
     */
    listByProjectId: async (
        projectId: string,
        params: {
            page: number;
            size: number;
            sort?: string;
            filter?: StockToolsQueryFilter;
        },
        signal?: AbortSignal
    ): Promise<PageStockToolsGetDTO> => {
        const response = await api.get<PageStockToolsGetDTO>(
            `/projects/${projectId}/stock/tools`,
            {
                params: {
                    page: params.page - 1,
                    size: params.size,
                    sort: params.sort || "id,desc",
                    ...params.filter,
                },
                signal,
            }
        );
        return response.data;
    },

    /**
     * Busca ferramenta em estoque de projeto
     * GET /projects/{project-id}/stock/tools/{tool-id}
     */
    getByIdInProjectStock: async (
        projectId: string,
        toolId: string
    ): Promise<StockToolGetDTO> => {
        const response = await api.get<StockToolGetDTO>(
            `/projects/${projectId}/stock/tools/${toolId}`
        );
        return response.data;
    },

    /**
     * Cria ferramenta em estoque de projeto
     * POST /projects/{project-id}/stock/tools
     */
    createInProjectStock: async (
        projectId: string,
        data: StockToolCreateDTO
    ): Promise<StockToolGetDTO> => {
        const response = await api.post<StockToolGetDTO>(
            `/projects/${projectId}/stock/tools`,
            data
        );
        return response.data;
    },

    /**
     * Atualiza ferramenta em estoque de projeto
     * PUT /projects/{project-id}/stock/tools/{tool-id}
     */
    updateInProjectStock: async (
        projectId: string,
        toolId: string,
        data: StockToolUpdateDTO
    ): Promise<StockToolGetDTO> => {
        const response = await api.put<StockToolGetDTO>(
            `/projects/${projectId}/stock/tools/${toolId}`,
            data
        );
        return response.data;
    },

    /**
     * Deleta ferramenta de estoque de projeto
     * DELETE /projects/{project-id}/stock/tools/{tool-id}
     */
    deleteFromProjectStock: async (
        projectId: string,
        toolId: string
    ): Promise<void> => {
        await api.delete(
            `/projects/${projectId}/stock/tools/${toolId}`
        );
    },
};
